import cx from 'classnames';
import { useCallback, useState, useEffect, useMemo } from 'react';
import InputMask from 'react-input-mask';
import Modal from 'react-modal';
import { activateUser } from '../../../../services/user';
import { isResponseOk } from '../../../../utility/isResponseOk';
import './styles.scss';

const ActivateModal = ({ closeModal, user, handleSuccess }) => {
  const [formData, setFormData] = useState({
    phone: '',
  });
  const [hasChanged, setHasChanged] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formData.phone.length > 0) {
      setHasChanged(true);
    }

    if ((formData.phone.length === 0 || formData.phone == '__-___-____') && hasChanged) {
      setErrors({ phone: ['This field cannot be empty'] });
    } else {
      setErrors({});
    }
  }, [formData]);

  const activateUserCallback = useCallback(async () => {
    try {
      const payload = {
        phone: formData.phone,
      };

      const response = await activateUser(user.id, payload);

      if (response) {
        closeModal();
        handleSuccess();
      }
    } catch (e) {
      if (e.response?.data) {
        setErrors(e.response.data);
      }
    }
  }, [user, formData]);

  const handleConfirm = () => {
    activateUserCallback();
  };

  const isValidForm = useMemo(() => {
    return formData.phone.length > 0 && (formData.phone.match(/\d/g) || []).length == 10 && !Object.keys(errors).length;
  }, [formData, errors]);

  return (
    <Modal
      className="modal-alert activate-user-modal"
      isOpen
      onRequestClose={closeModal}
      contentLabel="Activate user modal"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="32" height="32" rx="16" fill="#F4EBFF" />
          <rect x="2" y="2" width="32" height="32" rx="16" stroke="#F9F5FF" strokeWidth="4" />
          <path
            d="M20.7004 12.1504H15.3004C14.8033 12.1504 14.4004 12.5533 14.4004 13.0504V22.9504C14.4004 23.4474 14.8033 23.8504 15.3004 23.8504H20.7004C21.1974 23.8504 21.6004 23.4474 21.6004 22.9504V13.0504C21.6004 12.5533 21.1974 12.1504 20.7004 12.1504Z"
            stroke="#7F56D9"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M14.4004 13.9502H21.6004" stroke="#7F56D9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14.4004 22.0498H21.6004" stroke="#7F56D9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        <button className="btn btn-icon" onClick={closeModal}>
          <i className="icon-icon-x"></i>
        </button>
      </h2>

      <div>
        <h3>Phone Number Required</h3>
        <p className="modal-subtitle">To activate this user, a phone number must be added.Please enter a valid phone number to proceed.</p>
        <div className="activate-user-form">
          <div className="form-item">
            <label className="form-label">Phone Number</label>
            <div className={cx('form-control-item', { 'has-error': errors.phone })}>
              <InputMask
                id="signer_phone"
                placeholder="XXX-XXX-XXXX"
                mask="999-999-9999"
                value={formData.phone}
                onInput={(e) => setFormData({ ...formData, phone: e.target.value.slice(0, 12) })}
                className="form-control"
              ></InputMask>
              <svg
                width="24"
                className="activate-user-error-icon"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9998 8.00021V12.0002M11.9998 16.0002H12.0098M10.2898 2.8602L1.81978 17.0002C1.64514 17.3026 1.55274 17.6455 1.55177 17.9947C1.55079 18.3439 1.64127 18.6873 1.8142 18.9907C1.98714 19.2941 2.2365 19.547 2.53748 19.7241C2.83847 19.9012 3.18058 19.9964 3.52978 20.0002H20.4698C20.819 19.9964 21.1611 19.9012 21.4621 19.7241C21.7631 19.547 22.0124 19.2941 22.1854 18.9907C22.3583 18.6873 22.4488 18.3439 22.4478 17.9947C22.4468 17.6455 22.3544 17.3026 22.1798 17.0002L13.7098 2.8602C13.5315 2.56631 13.2805 2.32332 12.981 2.15469C12.6814 1.98605 12.3435 1.89746 11.9998 1.89746C11.656 1.89746 11.3181 1.98605 11.0186 2.15469C10.7191 2.32332 10.468 2.56631 10.2898 2.8602Z"
                  stroke="#B42318"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <p className="form-error">{(errors.phone && errors.phone[0]) || errors.phone?.phone}</p>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={closeModal}>
          Cancel
        </button>
        <button
          className="btn activate-user-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleConfirm();
          }}
          disabled={!isValidForm}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_14675_76888)">
              <path
                d="M18.3337 9.23355V10.0002C18.3326 11.7972 17.7507 13.5458 16.6748 14.9851C15.5988 16.4244 14.0864 17.4773 12.3631 17.9868C10.6399 18.4963 8.79804 18.4351 7.11238 17.8124C5.42673 17.1896 3.98754 16.0386 3.00946 14.5311C2.03138 13.0236 1.56682 11.2403 1.68506 9.44714C1.80329 7.65402 2.498 5.94715 3.66556 4.58111C4.83312 3.21506 6.41098 2.26303 8.16382 1.867C9.91665 1.47097 11.7505 1.65216 13.392 2.38355M18.3337 3.33355L10.0003 11.6752L7.50033 9.17521"
                stroke="#F9FAFB"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_14675_76888">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Activate
        </button>
      </div>
    </Modal>
  );
};

export default ActivateModal;
